// src/components/ScorecardFooter.js
import React, { useState, useEffect } from "react";
import { 
  FormControlLabel, 
  Switch, 
  Typography, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions 
} from "@mui/material";
import { useAuth } from "../../context/AuthContext";

const ScorecardFooter = ({
  selectedCourse,
  roundDate,
  isCompetition,
  setIsCompetition,
  competitionDeadline,      // End of competition window: Sunday at 23:59:59.999 Eastern
  numHoles,
  onSubmit,
  scorecardComplete,
  competitionWindowStart,   // Start of competition window: Monday at 00:00:00 Eastern
}) => {
  const { user } = useAuth();
  const [submissionAttempted, setSubmissionAttempted] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Helper: Convert a date string in "YYYY-MM-DD" (from the date input)
  // to a Date object, interpreting it as if it were in Eastern Time.
  const parseEasternDateFromInput = (dateString) => {
    // Replace dashes with slashes to force the string to be parsed as local time.
    // Then append " 00:00:00" to indicate midnight.
    const localFormat = dateString.replace(/-/g, "/") + " 00:00:00";
    const localDate = new Date(localFormat);
    // Now, convert that local date to Eastern Time.
    const easternString = localDate.toLocaleString("en-US", { timeZone: "America/New_York" });
    return new Date(easternString);
  };

  // Use our custom parser so that, for example, "2025-04-28" is interpreted as Monday, April 28, 2025 in Eastern Time.
  const roundDateObj = roundDate ? parseEasternDateFromInput(roundDate) : null;

  const isNineHole = numHoles === 9;
  const hasCourse = Boolean(selectedCourse);
  const hasRoundDate = Boolean(roundDate);
  
  // A round date is considered invalid if it's before the competition window start
  // or after the competition deadline.
  const roundDateInvalid =
    roundDateObj &&
    (roundDateObj.getTime() < competitionWindowStart.getTime() ||
     roundDateObj.getTime() > competitionDeadline.getTime());

  const handicapNotApproved = user && user.isHandicapApproved === false;

  const toggleWarnings = [];
  if (isNineHole) {
    toggleWarnings.push("Competition play requires a full 18-hole round.");
  }
  if (roundDateInvalid) {
    toggleWarnings.push(
      "Competition rounds can only be posted for rounds played between " +
      competitionWindowStart.toLocaleDateString("en-US", { timeZone: "America/New_York" }) +
      " and " +
      competitionDeadline.toLocaleDateString("en-US", { timeZone: "America/New_York" }) +
      "."
    );
  }
  if (handicapNotApproved) {
    toggleWarnings.push("Your handicap must be approved before you can submit a round for competition play.");
  }

  const submissionErrors = [];
  if (submissionAttempted) {
    if (!hasCourse) {
      submissionErrors.push("Don't be afraid to select a course!");
    }
    if (!hasRoundDate) {
      submissionErrors.push("Don't forget the round date!");
    }
    if (!scorecardComplete) {
      submissionErrors.push("I think you may have forgotten to fill in your entire scorecard, beast... Oops!");
    }
  }

  // Reset the competition toggle if disabling conditions are met.
  useEffect(() => {
    if ((isNineHole || roundDateInvalid || handicapNotApproved) && isCompetition) {
      setIsCompetition(false);
    }
  }, [isNineHole, roundDateInvalid, handicapNotApproved, isCompetition, setIsCompetition]);

  const handleClickSubmit = () => {
    setSubmissionAttempted(true);
    if (hasCourse && hasRoundDate && scorecardComplete) {
      setConfirmDialogOpen(true);
    }
  };

  const handleConfirmSubmit = () => {
    setConfirmDialogOpen(false);
    onSubmit();
  };

  return (
    <>
      <FormControlLabel
        control={
          <Switch
            checked={isCompetition}
            onChange={(e) => setIsCompetition(e.target.checked)}
            color="primary"
            disabled={isNineHole || roundDateInvalid || handicapNotApproved}
          />
        }
        label="Post For Competition Play"
        sx={{ mt: 2 }}
      />
      {/* Always display toggle warnings if any exist */}
      {toggleWarnings.map((msg, index) => (
        <Typography
          key={index}
          variant="body2"
          sx={{ mt: 1, color: (theme) => theme.palette.warning.main }}
        >
          {msg}
        </Typography>
      ))}
      {/* Display submission errors (only after attempt) */}
      {submissionErrors.map((err, index) => (
        <Typography
          key={index}
          variant="body2"
          sx={{ mt: 1, color: (theme) => theme.palette.warning.main }}
        >
          {err}
        </Typography>
      ))}
      <Button variant="contained" color="primary" onClick={handleClickSubmit} sx={{ mt: 2 }}>
        Submit Scorecard
      </Button>
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Submission</DialogTitle>
        <DialogContent>
          <Typography>
            Once you submit, you won't be able to edit your scores. Are you sure you want to submit your scorecard?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmSubmit} color="primary" variant="contained">
            Submit Scorecard
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ScorecardFooter;